import { apiFetch } from '@/lib/http/client';
import type { EntityKind } from '@/features/canvas/utils/node-types';

export interface ResolveQuery {
  id: string;
  kind?: EntityKind;
  title: string;
  hint?: string;
}

export interface MatchedResolution {
  id: string;
  outcome: 'matched';
  entityId: string;
  kind: EntityKind;
  confidence: number;
  // S7's response struct has `runner_up_score: Option<f32>` — wire format
  // may be `null` or absent. Keep this optional/nullable.
  runnerUpScore?: number | null;
}

export interface AmbiguousCandidate {
  entityId: string;
  kind: EntityKind;
  title: string;
  score: number;
}

export interface AmbiguousResolution {
  id: string;
  outcome: 'ambiguous';
  candidates: AmbiguousCandidate[];
}

export interface NoMatchResolution {
  id: string;
  outcome: 'no-match';
}

export type Resolution =
  | MatchedResolution
  | AmbiguousResolution
  | NoMatchResolution;

interface ResolveResponse {
  resolutions: Resolution[];
}

export async function resolveEntities(
  workspaceId: string,
  queries: ResolveQuery[],
): Promise<Resolution[]> {
  // apiFetch prepends /api automatically — the documented endpoint is
  // POST /api/entities/resolve, but the path passed here omits the /api prefix.
  // apiFetch also auto-injects Content-Type for non-FormData bodies, so don't
  // pass `headers: { 'Content-Type': '...' }` — redundant.
  const res = await apiFetch<ResolveResponse>('/entities/resolve', {
    method: 'POST',
    body: JSON.stringify({ workspaceId, queries }),
  });
  return res.resolutions;
}
